import { Controller } from "@hotwired/stimulus"

/**
 * Controller for filling form fields from external data sources
 * (Custom Reports <-> Configurable Timesheets)
 *
 * Usage:
 *   data-controller="fill-from-source"
 *   data-fill-from-source-type-value="vehicles|activities|timesheets_to_employees"
 *   data-fill-from-source-site-id-value="123" (optional)
 *   data-fill-from-source-date-value="2024-01-15" (optional)
 */
export default class extends Controller {
  static targets = ["button", "dropdown", "status", "sourceList"]
  static values = {
    type: String,
    siteId: Number,
    taskId: Number,
    date: String,
    blockId: Number,
    sourcesUrl: { type: String, default: "/data_linking/sources" },
    prefillUrl: { type: String, default: "/data_linking/prefill" },
    // "Kolonne übernehmen": CustomReportsController#activity_defaults of the report's parent
    crewUrl: String,
    // i18n strings
    loading: { type: String, default: "Laden..." },
    noSources: { type: String, default: "Keine Datenquellen gefunden" },
    selectSource: { type: String, default: "Quelle auswählen" },
    success: { type: String, default: "Daten übernommen" },
    error: { type: String, default: "Fehler beim Laden" },
    employeesImported: { type: String, default: "%{count} Mitarbeiter, %{hours}h übernommen" },
    // No German fallbacks: the partial always passes data_linking.crew_added / no_crew.
    crewAdded: String,
    noCrew: String,
  }

  connect() {
    this.sources = []
    this.isOpen = false
  }

  async toggle(event) {
    event.preventDefault()

    if (this.isOpen) {
      this.close()
    } else {
      await this.open()
    }
  }

  async open() {
    this.isOpen = true

    // Show loading state
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.remove("hidden")
      this.dropdownTarget.innerHTML = this.loadingHtml()
    }

    // Fetch available sources
    try {
      const params = this.buildSourceParams()
      const response = await fetch(`${this.sourcesUrlValue}?${params.toString()}`, {
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken,
        },
      })

      this.sources = await response.json()

      if (this.sources.length === 0) {
        this.dropdownTarget.innerHTML = this.noSourcesHtml()
      } else {
        this.dropdownTarget.innerHTML = this.sourceListHtml()
      }
    } catch (error) {
      console.error("Error fetching sources:", error)
      this.dropdownTarget.innerHTML = this.errorHtml()
    }
  }

  close() {
    this.isOpen = false
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.add("hidden")
    }
  }

  async selectSource(event) {
    event.preventDefault()

    const sourceId = event.currentTarget.dataset.sourceId
    if (!sourceId) return

    this.close()

    // Show loading on button
    const button = this.buttonTarget
    const originalHtml = button.innerHTML
    button.disabled = true
    button.innerHTML = this.loadingButtonHtml()

    try {
      const params = new URLSearchParams()
      params.append("type", this.typeValue)
      params.append("source_id", sourceId)

      // For timesheet_to_employees, add site_id and date
      if (this.typeValue === "timesheets_to_employees") {
        params.append("site_id", this.currentSiteId)
        params.append("date", this.currentDate)
      }

      const response = await fetch(`${this.prefillUrlValue}?${params.toString()}`, {
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken,
        },
      })

      const data = await response.json()
      this.fillForm(data)

      const sourceLabel =
        data.source_label || this.sources.find((s) => s.id == sourceId)?.label || ""
      this.showStatus("success", `${this.successValue} (${sourceLabel})`)
    } catch (error) {
      console.error("Error fetching prefill data:", error)
      this.showStatus("error", this.errorValue)
    } finally {
      button.disabled = false
      button.innerHTML = originalHtml
    }
  }

  // For timesheets_to_employees - direct fetch without source selection
  async fetchFromTimesheets(event) {
    event.preventDefault()

    const button = this.buttonTarget
    const originalHtml = button.innerHTML
    button.disabled = true
    button.innerHTML = this.loadingButtonHtml()

    try {
      const params = new URLSearchParams()
      params.append("type", "timesheets_to_employees")
      params.append("site_id", this.currentSiteId)
      params.append("date", this.currentDate)
      if (this.hasBlockIdValue && this.blockIdValue) params.append("block_id", this.blockIdValue)

      const response = await fetch(`${this.prefillUrlValue}?${params.toString()}`, {
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken,
        },
      })

      const data = await response.json()
      if (!response.ok) throw new Error(data.error || response.statusText)
      this.fillEmployeesBlock(data)

      const summary = data.summary
      const message = this.employeesImportedValue
        .replace("%{count}", summary.employee_count)
        .replace("%{hours}", summary.total_hours)
      this.showStatus("success", message)
    } catch (error) {
      console.error("Error fetching timesheet data:", error)
      this.showStatus("error", error.message || this.errorValue)
    } finally {
      button.disabled = false
      button.innerHTML = originalHtml
    }
  }

  // "Kolonne übernehmen": one activities row with the crew planned that day, the report
  // date and the tenant's default times — the date the report form currently holds.
  async fetchCrew(event) {
    event.preventDefault()

    const button = this.buttonTarget
    const originalHtml = button.innerHTML
    button.disabled = true
    button.innerHTML = this.loadingButtonHtml()

    try {
      const date =
        this.form?.querySelector('[name="custom_report[report_date]"]')?.value ||
        this.currentDate ||
        ""
      const response = await fetch(`${this.crewUrlValue}?date=${encodeURIComponent(date)}`, {
        headers: { Accept: "application/json", "X-CSRF-Token": this.csrfToken },
      })
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || response.statusText)
      if (!data.employee_ids?.length) {
        this.showStatus("error", this.noCrewValue)
        return
      }

      this.fillEmployeesBlock({ employees: [data] })
      this.showStatus("success", this.crewAddedValue.replace("%{count}", data.employee_ids.length))
    } catch (error) {
      console.error("Error fetching crew:", error)
      this.showStatus("error", error.message || this.errorValue)
    } finally {
      button.disabled = false
      button.innerHTML = originalHtml
    }
  }

  fillForm(data) {
    switch (this.typeValue) {
      case "vehicles":
        this.fillVehicles(data.vehicles)
        break
      case "activities":
        this.fillActivities(data)
        break
      case "timesheets_to_employees":
        this.fillEmployeesBlock(data)
        break
    }
  }

  // A report's vehicles block becomes Fahrzeug-Einsatz rows, when the form has them:
  // the vehicle and, from the report's von plus the vehicle's own hours, its window.
  // A vehicle the form does not offer (no Fahrzeug-Vorlage applies) is skipped; a row
  // without times is left for the user to complete.
  fillVehicles(vehicles, timeFrom) {
    const container = this.form?.querySelector('[data-fleet-usage-options-target="usageContainer"]')
    const template = container?.querySelector('template[data-nested-form-target="template"]')
    const links = container?.querySelector('[data-nested-form-target="links"]')
    if (!template || !links) return

    ;(vehicles || []).forEach((vehicle) => {
      const key = `report_${new Date().getTime()}_${Math.floor(Math.random() * 1e6)}`
      const holder = document.createElement("div")
      holder.innerHTML = template.innerHTML.replaceAll("NEW_RECORD", key)
      const row = holder.firstElementChild
      const fleetSelect = row?.querySelector(`[name="fleet_usages[${key}][fleet_id]"]`)
      if (!fleetSelect || !fleetSelect.querySelector(`option[value="${vehicle.fleet_id}"]`)) return

      fleetSelect.value = String(vehicle.fleet_id)
      const timeTo = this.addHours(timeFrom, vehicle.own_hours)
      if (timeFrom && timeTo) {
        const from = row.querySelector(`[name="fleet_usages[${key}][time_from]"]`)
        const to = row.querySelector(`[name="fleet_usages[${key}][time_to]"]`)
        if (from) from.value = timeFrom
        if (to) to.value = timeTo
      }
      links.insertAdjacentElement("beforebegin", row)
    })
  }

  // "07:00" + 2.5 → "09:30"; nothing for a missing start, no hours or a window past midnight.
  addHours(time, hours) {
    const amount = parseFloat(hours)
    if (!time || !amount || amount <= 0) return null

    const [h, m] = time.split(":").map((part) => parseInt(part, 10))
    const total = h * 60 + m + Math.round(amount * 60)
    if (Number.isNaN(total) || total >= 24 * 60) return null

    return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`
  }

  fillActivities(data) {
    const form = this.form
    if (!form) return

    // Setting the employees and the date below makes seasonal-default-times fetch the
    // work-time model's times, and those answers land after this method has returned —
    // replacing the report's times with the template default. While the report brings its
    // own times, hold that refresh off and drop any answer already on its way; once the
    // fill is done, the deferred refresh still brings the times the report left open
    // (rows that disagree yield none) to the new date and employee.
    const keep = ["time_from", "time_to", "time_break"].filter((field) => data[field])
    if (keep.length > 0) {
      form.dataset.defaultTimesSuspended = "true"
      form.dispatchEvent(new CustomEvent("default-times:superseded"))
    }

    try {
      this.applyActivities(data)
      if (data.vehicles?.length) this.fillVehicles(data.vehicles, data.time_from)
    } finally {
      if (keep.length > 0) {
        delete form.dataset.defaultTimesSuspended
        form.dispatchEvent(new CustomEvent("default-times:resumed", { detail: { keep } }))
      }
    }

    // Dispatch event for any additional handling
    this.element.dispatchEvent(
      new CustomEvent("fill-from-source:activities", {
        detail: data,
        bubbles: true,
      }),
    )
  }

  applyActivities(data) {
    // Fill employee_ids multi-select
    if (data.employee_ids && data.employee_ids.length > 0) {
      this.setMultiSelectField("employee_ids", data.employee_ids)
    }

    // Fill time fields (only if consistent values exist)
    if (data.time_from) {
      this.setTimeField("time_from", data.time_from)
    }
    if (data.time_to) {
      this.setTimeField("time_to", data.time_to)
    }
    if (data.time_break) {
      this.setTimeField("time_break", data.time_break)
    }

    // Fill site_id
    if (data.site_id) {
      this.setSelectField("site_id", data.site_id)
    }

    // Fill task_ids
    if (data.task_ids && data.task_ids.length > 0) {
      this.setMultiSelectField("task_ids", data.task_ids)
    }

    // Fill reported_date
    if (data.reported_date) {
      this.setDateField("reported_date", data.reported_date)
    }
  }

  fillEmployeesBlock(data) {
    // The block's nested form. Matched as one controller among several: the activities
    // block also runs activity-hours on the same element.
    // Nothing to fill into is a failure, not a success with zero rows (fetchFromTimesheets shows it).
    const container = this.element.closest('[data-controller~="nested-form"]')
    const template = container?.querySelector('template[data-nested-form-target="template"]')
    if (!template) throw new Error(this.errorValue)

    const employees = data.employees || []

    // Add a row for each employee. The key must stay an integer, like nested-form's own:
    // Rails drops a nested-attributes row whose key is not one ("1759….6858" never saved).
    employees.forEach((emp, index) => {
      const content = template.innerHTML.replaceAll(/NEW_RECORD/g, String(Date.now() * 100 + index))

      // Create a temporary container to parse the HTML
      const tempDiv = document.createElement("div")
      tempDiv.innerHTML = content

      // Fill in the data
      const row = tempDiv.firstElementChild

      // Set employee_ids
      const employeeSelect = row.querySelector('[name*="[employee_ids]"]')
      if (employeeSelect && emp.employee_ids) {
        if (employeeSelect.multiple) {
          Array.from(employeeSelect.options).forEach((option) => {
            option.selected = emp.employee_ids.includes(parseInt(option.value))
          })
        } else if (emp.employee_ids[0]) {
          employeeSelect.value = emp.employee_ids[0]
        }
      }

      // The rest by column name: an employees block takes hours and role, an activities
      // block one row per booking with its date, von/bis, Pause and description. A column
      // the block does not show has no input and is skipped.
      const filled = []
      ;["date", "from", "to", "break", "hours", "role", "description"].forEach((key) => {
        const value = emp[key]
        if (value === undefined || value === null || value === "") return

        const input = row.querySelector(`[name$="[data][${key}]"]`)
        if (!input) return
        input.value = value
        filled.push(input)
      })

      // Insert where nested-form#add_association would: into the block's row list when
      // the "add" link sits outside it, else right before the link.
      const list = container.querySelector('[data-nested-form-target="list"]')
      const linksTarget = container.querySelector('[data-nested-form-target="links"]')
      if (list && !list.contains(linksTarget)) {
        list.append(row)
      } else if (linksTarget) {
        linksTarget.insertAdjacentElement("beforebegin", row)
      }

      // Announce the values once the row is in the page and its controllers have connected
      // (one tick later): the date picker shows what its hidden ISO field now holds, and the
      // block recalculates its hours and total.
      setTimeout(() => {
        filled.forEach((input) => input.dispatchEvent(new Event("change", { bubbles: true })))
      }, 0)
    })

    // Dispatch event
    this.element.dispatchEvent(
      new CustomEvent("fill-from-source:employees", {
        detail: data,
        bubbles: true,
      }),
    )
  }

  // === Helper methods ===

  buildSourceParams() {
    const params = new URLSearchParams()
    params.append("type", this.typeValue)

    const siteId = this.currentSiteId
    if (siteId) params.append("site_id", siteId)

    const taskId = this.currentTaskId
    if (taskId) params.append("task_id", taskId)

    const date = this.currentDate
    if (date) params.append("date", date)

    return params
  }

  get currentSiteId() {
    if (this.hasSiteIdValue && this.siteIdValue) return this.siteIdValue

    // Try to get from form
    const siteInput = this.form?.querySelector('[name*="[site_id]"]')
    return siteInput?.value || null
  }

  get currentTaskId() {
    if (this.hasTaskIdValue && this.taskIdValue) return this.taskIdValue
    return null
  }

  get currentDate() {
    if (this.hasDateValue && this.dateValue) return this.dateValue

    // Try to get from form
    const dateInput =
      this.form?.querySelector('[name*="[reported_date]"]') ||
      this.form?.querySelector('[name*="[report_date]"]')
    return dateInput?.value || null
  }

  get form() {
    return this.element.closest("form") || document.querySelector("form")
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  setTimeField(fieldName, value) {
    if (!value) return

    const input = this.findFormInput(fieldName)
    if (input) {
      input.value = value
      input.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  setDateField(fieldName, value) {
    if (!value) return

    const input = this.findFormInput(fieldName)
    if (input) {
      // Handle ISO date format
      const dateStr = typeof value === "string" ? value.split("T")[0] : value
      input.value = dateStr
      input.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  setSelectField(fieldName, value) {
    if (!value) return

    const input = this.findFormInput(fieldName)
    if (input) {
      const slim = this.findSlimSelect(input)
      if (slim) {
        slim.setSelected(String(value))
      } else {
        input.value = value
      }
      input.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  setMultiSelectField(fieldName, values) {
    if (!values || !Array.isArray(values)) return

    const form = this.form
    if (!form) return

    const select = form.querySelector(`select[name*="[${fieldName}]"]`)
    if (select && select.multiple) {
      const stringValues = values.map((v) => String(v))

      const slim = this.findSlimSelect(select)
      if (slim) {
        slim.setSelected(stringValues)
      } else {
        Array.from(select.options).forEach((option) => {
          option.selected = stringValues.includes(option.value)
        })
      }
      select.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  findSlimSelect(selectElement) {
    const controller = this.application.getControllerForElementAndIdentifier(
      selectElement,
      "select",
    )
    return controller?.slimselect || null
  }

  findFormInput(fieldName) {
    const form = this.form
    if (!form) return null

    return (
      form.querySelector(`[name*="[${fieldName}]"]`) || form.querySelector(`[name="${fieldName}"]`)
    )
  }

  showStatus(type, message) {
    if (!this.hasStatusTarget) return

    const statusEl = this.statusTarget
    statusEl.classList.remove("hidden", "text-green-600", "text-blue-600", "text-red-600")

    switch (type) {
      case "success":
        statusEl.classList.add("text-green-600")
        break
      case "info":
        statusEl.classList.add("text-blue-600")
        break
      case "error":
        statusEl.classList.add("text-red-600")
        break
    }

    statusEl.textContent = message

    setTimeout(() => {
      statusEl.classList.add("hidden")
    }, 5000)
  }

  // === HTML Templates ===

  loadingHtml() {
    return `
      <div class="p-3 text-center text-gray-500">
        <svg class="animate-spin h-5 w-5 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span class="mt-2 block text-sm">${this.loadingValue}</span>
      </div>
    `
  }

  loadingButtonHtml() {
    return `
      <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-current inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      ${this.loadingValue}
    `
  }

  noSourcesHtml() {
    return `
      <div class="p-3 text-center text-gray-500 text-sm">
        ${this.noSourcesValue}
      </div>
    `
  }

  errorHtml() {
    return `
      <div class="p-3 text-center text-red-500 text-sm">
        ${this.errorValue}
      </div>
    `
  }

  sourceListHtml() {
    const items = this.sources
      .map(
        (source) => `
      <button type="button"
              class="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
              data-action="click->fill-from-source#selectSource"
              data-source-id="${source.id}">
        <div class="font-medium text-gray-900">${this.escapeHtml(source.label)}</div>
        ${source.report_date ? `<div class="text-xs text-gray-500">${this.escapeHtml(source.report_date)}</div>` : ""}
      </button>
    `,
      )
      .join("")

    return `
      <div class="py-1 max-h-60 overflow-y-auto">
        ${items}
      </div>
    `
  }

  // Escape HTML to prevent XSS
  escapeHtml(text) {
    if (!text) return ""
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
